import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { adminPlansApi } from '../api/adminApi';
import { Plan, LoadingStatus } from '../../types';

interface AdminPlansState { plans: Plan[]; status: LoadingStatus; error: string | null; }
const initialState: AdminPlansState = { plans: [], status: 'idle', error: null };

export const fetchAdminPlans = createAsyncThunk('adminPlans/fetch', async (_, { rejectWithValue }) => {
  try { return (await adminPlansApi.list()).data as Plan[]; }
  catch (err: unknown) { const e = err as { response?: { data?: { message?: string } } }; return rejectWithValue(e.response?.data?.message || 'Failed'); }
});

export const createPlan = createAsyncThunk('adminPlans/create', async (data: Record<string, unknown>, { rejectWithValue }) => {
  try { return (await adminPlansApi.create(data)).data as Plan; }
  catch (err: unknown) { const e = err as { response?: { data?: { message?: string } } }; return rejectWithValue(e.response?.data?.message || 'Failed'); }
});

export const updatePlan = createAsyncThunk('adminPlans/update', async ({ id, data }: { id: string; data: Record<string, unknown> }, { rejectWithValue }) => {
  try { return (await adminPlansApi.update(id, data)).data as Plan; }
  catch (err: unknown) { const e = err as { response?: { data?: { message?: string } } }; return rejectWithValue(e.response?.data?.message || 'Failed'); }
});

export const deletePlan = createAsyncThunk('adminPlans/delete', async (id: string, { rejectWithValue }) => {
  try { await adminPlansApi.delete(id); return id; }
  catch (err: unknown) { const e = err as { response?: { data?: { message?: string } } }; return rejectWithValue(e.response?.data?.message || 'Failed'); }
});

const slice = createSlice({
  name: 'adminPlans',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchAdminPlans.pending, (state) => { state.status = 'loading'; state.error = null; })
      .addCase(fetchAdminPlans.fulfilled, (state, { payload }) => { state.plans = payload; state.status = 'succeeded'; })
      .addCase(fetchAdminPlans.rejected, (state, { payload }) => {
        state.status = 'failed';
        state.error = (payload as string) || 'Failed to load plans';
      })
      .addCase(createPlan.fulfilled, (state, { payload }) => { state.plans.push(payload); })
      .addCase(updatePlan.fulfilled, (state, { payload }) => {
        const idx = state.plans.findIndex((p) => p._id === payload._id);
        if (idx !== -1) state.plans[idx] = payload;
      })
      .addCase(deletePlan.fulfilled, (state, { payload }) => { state.plans = state.plans.filter((p) => p._id !== payload); });
  },
});

export default slice.reducer;
export const selectAdminPlans = (s: { adminPlans: AdminPlansState }) => s.adminPlans.plans;
export const selectAdminPlansStatus = (s: { adminPlans: AdminPlansState }) => s.adminPlans.status;
export const selectAdminPlansError = (s: { adminPlans: AdminPlansState }) => s.adminPlans.error;
